import React, { FC } from 'react'
import { TopBlogOuter, TopBlogSection } from './style'

//* utils
import { UseCaseTypes } from '../../../../constants/blogUseCase'
import { LocaleCodes } from '../../../../constants/localization.constants'
import { ComponentAnnouncement } from '../../../../models/components/announcement'
import { ComponentMainNavBanner } from '../../../../models/components/mainNavBanner'
import { MenuGroup } from '../../../../models/others/menuGroup'
import { generateAdData } from '../../../../utils/functions.utils'
import { generateBlogHero } from './utils/blogHero'
import { fillInBlogFields } from './utils/helper'

//* components
import BlogListing from '../../../global/blogListing'
import GlobalContainer from '../../../global/globalContainer'
import Layout from '../../../layouts/layout'
import BlogNavigation from '../navigation'

type Props = {
  pageContext: any
  headerData?:
    | {
        allContentfulNavMenu: {
          nodes: MenuGroup[]
        }
      }
    | undefined
  footerData?:
    | {
        allContentfulNavMenu: {
          nodes: MenuGroup[]
        }
      }
    | undefined
  announcementData?: ComponentAnnouncement
  mainNavBannerData?: ComponentMainNavBanner
  browserLanguageData?: any
}

const MainBlogLanding: FC<Props> = ({
  pageContext,
  headerData,
  footerData,
  announcementData,
  mainNavBannerData,
  browserLanguageData,
}) => {
  if (!pageContext) return null

  let primaryPost = {}
  const isMainBlog = pageContext.homePageData?.useCase === UseCaseTypes.MAIN_BLOG
  const isCommunityBlog = pageContext.homePageData?.useCase === UseCaseTypes.COMMUNITY_BLOG
  const secondaryPosts = pageContext.secondaryPosts || []
  const featuredResource = pageContext.blogFeaturedResource || null
  const defaultLocale = pageContext.pageLocale || LocaleCodes.ENGLISH.code
  const mainBlogData = {
    pageNumber: pageContext.pageData?.pageNumber || 0,
    subscribe: isMainBlog || isCommunityBlog ? pageContext.pageData?.subscribe : null,
    homePage: {
      useCase: pageContext.homePageData?.useCase || '',
      listingTitle: pageContext.homePageData?.listingTitle || '',
      slug: pageContext.homePageData?.slug || '',
      seoMetadata: pageContext.homePageData?.seoMetadata || '',
    },
    blogPosts: pageContext.pageData?.blogPosts || [],
    totalPages: pageContext.pageData?.totalPages || 0,
    path: pageContext.pageData?.path || '/blog',
    totalBlogLandingPages: pageContext.pageData?.totalBlogLandingPages || 0,
  }

  if (pageContext.primaryPost?.node) {
    primaryPost = fillInBlogFields(
      pageContext.primaryPost.node,
      mainBlogData.homePage.useCase,
      defaultLocale
    )
  }

  if (primaryPost) mainBlogData.totalPages -= 1
  if (secondaryPosts) mainBlogData.totalPages -= secondaryPosts.length

  const getFeaturedAd = () => {
    if (featuredResource) {
      return generateAdData(featuredResource)
    }
  }

  //* disable secondary posts if there are less than 3. merge with main lisitngs
  const mainListing =
    secondaryPosts && secondaryPosts.length < 3
      ? [...mainBlogData.blogPosts.map((p: any) => p.node)]
      : mainBlogData.blogPosts.map((p: any) => p.node)

  return (
    <Layout.Template
      lang={defaultLocale}
      pageType="blog"
      headerData={headerData}
      footerData={footerData}
      announcementData={announcementData}
      mainNavBannerData={mainNavBannerData}
      browserLanguageData={browserLanguageData}>
      <div data-cy="MainBlogLanding">
        {pageContext.blogNav && (
          <BlogNavigation
            blogNav={pageContext.blogNav}
            pagePath={mainBlogData.path}
            useCase={mainBlogData.homePage.useCase}
          />
        )}

        {mainBlogData.blogPosts.length && (
          <>
            {mainBlogData.pageNumber === 1 && (
              <TopBlogOuter>
                <GlobalContainer>
                  <TopBlogSection>
                    {generateBlogHero(
                      pageContext,
                      primaryPost,
                      mainBlogData,
                      mainBlogData.homePage.useCase,
                      defaultLocale,
                      isMainBlog
                    )}
                  </TopBlogSection>
                </GlobalContainer>
              </TopBlogOuter>
            )}

            <BlogListing
              posts={mainListing}
              totalPages={mainBlogData.totalPages}
              pageNumber={mainBlogData.pageNumber}
              slug={mainBlogData.homePage.slug}
              subscribe={mainBlogData.subscribe}
              seoData={mainBlogData.homePage.seoMetadata}
              collectionRowData={isMainBlog ? pageContext.collectionRowData : null}
              numListingPages={mainBlogData.totalBlogLandingPages}
              blogFRData={isMainBlog ? getFeaturedAd() : null}
              locale={defaultLocale}
              listingTitle={mainBlogData.homePage.listingTitle}
              isMainBlog={isMainBlog}
            />
          </>
        )}
      </div>
    </Layout.Template>
  )
}

export default MainBlogLanding
